export default function ComparisonPricing({
  title,
  subtitle,
  leftCard,
  rightCard,
}) {
  return (
    <section className="py-[100px] px-6 lg:px-10 bg-base-white">
      <div className="max-w-[1100px] mx-auto">
        <h2 className="font-inter text-[24px] sm:text-[32px] lg:text-[38px] font-black tracking-[-1.5px] leading-[1.15] mb-4 text-center fade-in">
          {title}
        </h2>
        <p className="font-inter text-base text-gray-cool-500 leading-[1.7] max-w-[700px] mx-auto mb-12 text-center fade-in">
          {subtitle}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 items-start fade-in">
          {/* Left Card (Featured / Ledgerowl) */}
          <div className="bg-base-white rounded-[20px] p-10 border border-blue-primary-500 shadow-[0_8px_32px_rgba(0,116,230,0.12)] relative transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(0,0,0,0.08)]">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-primary-500 text-white text-[11px] font-bold px-4 py-1 rounded-full tracking-[1px]">
              RECOMMENDED
            </div>
            <div className="flex items-center gap-2 mb-5">
              <span className="font-inter text-xl font-extrabold tracking-[-0.5px] text-blue-primary-900">
                {leftCard.brand}
              </span>
            </div>
            <div className="text-[13px] font-semibold text-gray-cool-500 mb-4">
              {leftCard.tagline}
            </div>
            <div className="mb-1">
              <span className="font-inter text-[36px] font-black tracking-[-2px] text-gray-cool-900">
                {leftCard.price}
              </span>
              <span className="text-base font-medium text-gray-cool-500">
                {leftCard.priceSuffix}
              </span>
            </div>
            {leftCard.priceNote && (
              <p className="text-[13px] text-blue-primary-500 font-semibold mb-4">
                {leftCard.priceNote}
              </p>
            )}
            {leftCard.features && (
              <div className="mt-4 space-y-2">
                {leftCard.features.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-sm text-gray-cool-700"
                  >
                    <span className="text-blue-primary-500 font-bold">✓</span>
                    {f}
                  </div>
                ))}
              </div>
            )}
            {leftCard.description && (
              <p className="text-sm text-gray-cool-500 leading-[1.7] mt-4">
                {leftCard.description}
              </p>
            )}
          </div>

          {/* VS Divider */}
          <div className="flex items-center justify-center self-center">
            <div className="w-14 h-14 rounded-full bg-cream border-2 border-gray-cool-200 flex items-center justify-center font-extrabold text-base text-gray-cool-500">
              VS
            </div>
          </div>

          {/* Right Card (Competitor) */}
          <div className="bg-base-white rounded-[20px] p-10 border border-gray-cool-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(0,0,0,0.08)]">
            <div className="flex items-center gap-2 mb-5">
              <span
                className="font-inter text-xl font-extrabold tracking-[-0.5px]"
                style={rightCard.brandColor ? { color: rightCard.brandColor } : {}}
              >
                {rightCard.brand}
              </span>
            </div>
            <div className="text-[13px] font-semibold text-gray-cool-500 mb-4">
              {rightCard.tagline}
            </div>
            <div className="mb-1">
              <span className="font-inter text-[36px] font-black tracking-[-2px] text-gray-cool-900">
                {rightCard.price}
              </span>
              <span className="text-base font-medium text-gray-cool-500">
                {rightCard.priceSuffix}
              </span>
            </div>
            {rightCard.priceNote && (
              <p className="text-[13px] text-semantic-notice-500 font-semibold mb-4">
                {rightCard.priceNote}
              </p>
            )}

            {/* Hidden cost warning box */}
            {rightCard.hiddenCost && (
              <div className="bg-semantic-notice-500/[0.08] border border-semantic-notice-500/20 rounded-xl p-4 mt-4">
                <div className="text-[12px] font-bold uppercase tracking-[1px] text-semantic-notice-500 mb-2 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {rightCard.hiddenCost.label}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-[28px] font-black text-gray-cool-900 tracking-[-1px]">
                    {rightCard.hiddenCost.amount}
                  </span>
                  <span className="text-sm text-gray-cool-500">
                    {rightCard.hiddenCost.suffix}
                  </span>
                </div>
                <p className="text-[12px] text-gray-cool-500 mt-1 leading-[1.4]">
                  {rightCard.hiddenCost.detail}
                </p>
              </div>
            )}

            {/* True cost box */}
            {rightCard.trueCost && (
              <div className="bg-gray-cool-100 rounded-[10px] p-3.5 mt-3 text-center">
                <div className="text-[11px] font-bold uppercase tracking-[1px] text-gray-cool-500 mb-1">
                  True monthly cost
                </div>
                <div className="text-[24px] font-black text-semantic-negative-500 tracking-[-1px]">
                  {rightCard.trueCost.amount}
                  <span className="text-[13px] font-medium text-gray-cool-500">
                    {rightCard.trueCost.suffix}
                  </span>
                </div>
                <div className="text-[11px] text-gray-cool-500 mt-0.5">
                  {rightCard.trueCost.detail}
                </div>
              </div>
            )}

            {/* Features list for outsourcing variant */}
            {rightCard.features && (
              <div className="mt-4 space-y-2">
                {rightCard.features.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-sm text-gray-cool-500"
                  >
                    <span>{f.icon || "⚠️"}</span>
                    {f.text}
                  </div>
                ))}
              </div>
            )}

            {rightCard.description && (
              <p className="text-sm text-gray-cool-500 leading-[1.7] mt-4">
                {rightCard.description}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
